import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import Images from 'assets/images/imageData';
import './TableItems.css';

/**
 * Renders a single action item as Link or button.
 */
const ActionItem = ({ action }) => {
  const hasLink = action.to != null || (action.link && action.link !== '');
  const to = action.to != null
    ? (typeof action.to === 'string' ? { pathname: action.to } : action.to)
    : hasLink && action.link
      ? (typeof action.link === 'string' ? { pathname: action.link } : { pathname: action.link, ...action.link })
      : null;

  const handleClick = (e) => {
    if (action.onClick) {
      if (e) e.preventDefault();
      action.onClick(e);
    }
  };

  if (to && !action.onClick) {
    return (
      <Dropdown.Item as={Link} to={to}>
        {action.name}
      </Dropdown.Item>
    );
  }
  return (
    <Dropdown.Item onClick={handleClick}>
      {action.name}
    </Dropdown.Item>
  );
};

ActionItem.propTypes = {
  action: PropTypes.shape({
    name: PropTypes.string.isRequired,
    link: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onClick: PropTypes.func,
    rowData: PropTypes.any,
  }),
};

/**
 * Table body: rows and cells from data + columns or headers.
 * - With columns: data + columns (each column: key, label, optional format(value, row), optional getActions(row))
 * - With headers: data + headers (same order as object keys; row may have Action/Actions array)
 */
const TableItems = ({
  data = [],
  headers = [],
  columns: columnsProp = [],
  serialNo = false,
  srNo = 1,
  emptyMessage = 'No data found',
  emptyImage = true,
  className = '',
}) => {
  const useColumns = columnsProp && columnsProp.length > 0;
  const wrapperRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isAtRightEnd, setIsAtRightEnd] = useState(true);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const scrollable = el.scrollWidth > el.clientWidth;
      setIsScrollable(scrollable);
      const atRight = scrollable && el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setIsAtRightEnd(atRight);
    };

    updateScrollState();
    const raf = requestAnimationFrame(() => updateScrollState());

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    el.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className={`table-section-empty-state ${className}`.trim()}>
        {emptyImage && Images?.NoData ? (
          <img src={Images.NoData} alt="" width="100%" />
        ) : (
          <div className="table-section-empty-state-text">{emptyMessage}</div>
        )}
      </div>
    );
  }

  const keys = useColumns ? columnsProp.map((c) => c.key) : Object.keys(data[0]);
  const columnLabels = useColumns ? columnsProp.map((c) => c.label) : (
    headers.length >= keys.length
      ? headers
      : [...headers, ...keys.slice(headers.length).map((k) => k.replace(/_/g, ' '))]
  );

  const renderCell = (row, colIndex, rowIndex) => {
    const col = useColumns ? columnsProp[colIndex] : { key: keys[colIndex], label: columnLabels[colIndex] };
    const key = col.key;
    const label = col.label;

    if (useColumns && typeof col.getActions === 'function') {
      const actions = col.getActions(row);
      if (!Array.isArray(actions) || actions.length === 0) return <td key={key} className="table-section-cell table-section-cell-actions">—</td>;
      return (
        <td key={key} className="table-section-cell table-section-cell-actions">
          <Dropdown className="table-section-actions-dropdown" alignRight>
            <Dropdown.Toggle variant="outline" size="sm" className="table-section-actions-toggle">
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {actions.map((action, i) => (
                <ActionItem key={i} action={action} />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      );
    }

    const value = row[key];
    const isActionKey = !useColumns && (key === 'Action' || key === 'Actions');
    if (isActionKey && Array.isArray(value) && value.length > 0) {
      return (
        <td key={key} className="table-section-cell table-section-cell-actions">
          <Dropdown className="table-section-actions-dropdown" alignRight>
            <Dropdown.Toggle variant="outline" size="sm" className="table-section-actions-toggle">
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {value.map((action, i) => (
                <ActionItem key={i} action={action} />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </td>
      );
    }

    const display = useColumns && typeof col.format === 'function'
      ? col.format(value, row)
      : (value != null && value !== '' ? String(value) : '—');
    const isStatusColumn = key === 'status' || label === 'Status';
    const statusType = (value === 'active' || display === 'Active') ? 'active' : (value === 'inactive' || display === 'Inactive' || value == null ? 'inactive' : null);
    const statusClass = isStatusColumn && statusType
      ? `table-section-status table-section-status-${statusType}`
      : null;
    return (
      <td key={key} className="table-section-cell" data-title={label}>
        {statusClass ? (
          <span className={statusClass}>{display}</span>
        ) : (
          display
        )}
      </td>
    );
  };

  const showLeftShadow = isScrollable && !isAtRightEnd;

  return (
    <div
      ref={wrapperRef}
      className={`table-section-table-wrapper ${isScrollable ? 'table-section-table-wrapper--scrollable' : ''} ${showLeftShadow ? 'table-section-table-wrapper--show-actions-shadow' : ''} ${className}`.trim()}
    >
      <table className="table-section-table panel-table table table-striped">
        <thead>
          <tr>
            {serialNo && <th className="table-section-th-sno">S.No.</th>}
            {keys.map((key, i) => {
              const isActionsColumn = useColumns && columnsProp[i] && typeof columnsProp[i].getActions === 'function';
              return (
                <th
                  key={key}
                  className={`table-section-th${isActionsColumn ? ' table-section-th-actions' : ''}`}
                >
                  {columnLabels[i] || key}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="table-section-row">
              {serialNo && (
                <td className="table-section-cell table-section-cell-sno">
                  {srNo + rowIndex}.
                </td>
              )}
              {keys.map((_, colIndex) => renderCell(row, colIndex, rowIndex))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

TableItems.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  headers: PropTypes.arrayOf(PropTypes.string),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      format: PropTypes.func,
      getActions: PropTypes.func,
    })
  ),
  serialNo: PropTypes.bool,
  srNo: PropTypes.number,
  emptyMessage: PropTypes.string,
  emptyImage: PropTypes.bool,
  className: PropTypes.string,
};

export default TableItems;
